import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Car, Clock, ShieldCheck, BarChart, Users, Briefcase, Building } from 'lucide-react';

// You'll need to create or import these images
import dealershipHero from './images/dealership1.webp'; // Replace with your image
import fleetImage from './images/fleet.jpg'; // Replace with your image
import dealershipImage from './images/dealership.jpg'; // Replace with your image

const DealershipFleetPage = ({ isMobile }) => {
  return (
    <div className="min-h-screen bg-white pt-16 md:pt-20">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="h-64 md:h-96 relative">
          {/* Background image */}
          <div className="absolute inset-0">
            <img 
              src={dealershipHero} 
              alt="Professional Dealership & Fleet Detailing" 
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = 'https://placehold.co/1200x600/2a87a8/white?text=Dealership+%26+Fleet+Solutions';
              }}
            />
            <div className="absolute inset-0 bg-black/50"></div>
          </div>
          
          {/* Hero content */}
          <div className="absolute inset-0 flex items-center">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl">
                <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">Business Detailing Solutions</h1>
                <p className="text-white/90 text-lg md:text-xl">
                  Specialized detailing services for dealerships, fleet operators, and businesses.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Introduction Section */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-800">Elevate Your Business with Premium Detailing</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto mb-8"></div>
            <p className="text-gray-600 text-lg mb-8">
              JP Auto Detailing offers specialized solutions for businesses that need to maintain a fleet of vehicles or 
              keep their inventory looking pristine. Our mobile detailing services come to your location, 
              minimizing downtime and maximizing efficiency.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mt-12">
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Calendar className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="text-gray-800 font-semibold mb-2">Flexible Scheduling</h3>
                <p className="text-gray-600 text-sm text-center">Tailored to your business hours</p>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Building className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="text-gray-800 font-semibold mb-2">On-site Service</h3>
                <p className="text-gray-600 text-sm text-center">We come to your location</p>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="text-gray-800 font-semibold mb-2">Multiple Vehicle Discounts</h3>
                <p className="text-gray-600 text-sm text-center">Significant savings for fleets</p>
              </div>
              
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <BarChart className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="text-gray-800 font-semibold mb-2">Customized Plans</h3>
                <p className="text-gray-600 text-sm text-center">Solutions that fit your needs</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Fleet Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-16">
            <div className="md:w-1/2">
              <img 
                src={fleetImage} 
                alt="Fleet Detailing Services" 
                className="rounded-xl shadow-lg"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://placehold.co/600x400/2a87a8/white?text=Fleet+Detailing';
                }}
              />
            </div>
            
            <div className="md:w-1/2">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Fleet Maintenance Solutions</h2>
              <p className="text-gray-600 mb-6">
                Keep your company vehicles looking professional and well-maintained with our specialized fleet detailing services. 
                We understand that your vehicles represent your brand, and we ensure they always make a positive impression.
              </p>
              
              <ul className="space-y-4">
                <li className="flex items-start">
                  <ShieldCheck className="text-blue-500 h-6 w-6 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Regular Maintenance Programs</h3>
                    <p className="text-gray-600 text-sm">Custom schedules to keep your fleet consistently clean</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Car className="text-blue-500 h-6 w-6 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-800">All Vehicle Types</h3>
                    <p className="text-gray-600 text-sm">Cars, trucks, vans, and specialty vehicles</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Clock className="text-blue-500 h-6 w-6 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Minimal Downtime</h3>
                    <p className="text-gray-600 text-sm">Efficient servicing to keep your fleet operational</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Briefcase className="text-blue-500 h-6 w-6 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Business Account Benefits</h3>
                    <p className="text-gray-600 text-sm">Preferred scheduling, volume discounts, and corporate billing</p>
                  </div>
                </li>
              </ul>
              
              <div className="mt-8">
                <Link to="/fleet-quote" className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-6 rounded-lg inline-block transition-colors">
                  Request Fleet Quote
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Dealership Services Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row-reverse items-center gap-8 md:gap-16">
            <div className="md:w-1/2">
              <img 
                src={dealershipImage} 
                alt="Dealership Detailing Services" 
                className="rounded-xl shadow-lg"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://placehold.co/600x400/2a87a8/white?text=Dealership+Services';
                }}
              />
            </div>
            
            <div className="md:w-1/2">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Dealership Enhancement Services</h2>
              <p className="text-gray-600 mb-6">
                Make every vehicle on your lot shine with our comprehensive dealership detailing services. 
                From new arrivals to certified pre-owned vehicles, we help maximize their appeal and value.
              </p>
              
              <ul className="space-y-4">
                <li className="flex items-start">
                  <ShieldCheck className="text-blue-500 h-6 w-6 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-800">New Inventory Preparation</h3>
                    <p className="text-gray-600 text-sm">Make new vehicles showroom-ready</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Car className="text-blue-500 h-6 w-6 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Used Vehicle Reconditioning</h3>
                    <p className="text-gray-600 text-sm">Restore pre-owned vehicles to peak condition</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Clock className="text-blue-500 h-6 w-6 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Quick Turnaround</h3>
                    <p className="text-gray-600 text-sm">Fast service to minimize inventory holding time</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <Briefcase className="text-blue-500 h-6 w-6 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-gray-800">Volume Pricing</h3>
                    <p className="text-gray-600 text-sm">Competitive rates for dealership quantities</p>
                  </div>
                </li>
              </ul>
              
              <div className="mt-8">
                <Link to="/dealership-quote" className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-6 rounded-lg inline-block transition-colors">
                  Request Dealership Quote
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Testimonials Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-800">What Our Business Clients Say</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-cyan-400 mx-auto"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <span className="text-blue-600 font-bold text-xl">C</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Chicago Fleet Services</h3>
                  <p className="text-gray-600 text-sm">Fleet Manager</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "JP Auto Detailing has been maintaining our delivery vehicles for over a year. Their attention to detail and 
                flexibility with our busy schedule has made them an invaluable partner."
              </p>
            </div>
            
            {/* Testimonial 2 */}
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <span className="text-blue-600 font-bold text-xl">N</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">North Side Auto Group</h3>
                  <p className="text-gray-600 text-sm">Sales Director</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "Since working with JP Auto Detailing, our vehicle turnaround time has decreased while the quality of our 
                presentations has improved. They're reliable, professional, and thorough."
              </p>
            </div>
            
            {/* Testimonial 3 */}
            <div className="bg-white p-6 rounded-xl shadow-md">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                  <span className="text-blue-600 font-bold text-xl">L</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Lakeview Executive Cars</h3>
                  <p className="text-gray-600 text-sm">Operations Manager</p>
                </div>
              </div>
              <p className="text-gray-600 italic">
                "Our executive car service demands the highest standards of cleanliness. JP Auto Detailing consistently 
                meets and exceeds our expectations, keeping our luxury fleet immaculate for our clientele."
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-cyan-500 relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/3 translate-x-1/3 blur-2xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full translate-y-1/3 -translate-x-1/3 blur-2xl"></div>
        
        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Elevate Your Business Image?</h2>
            <p className="text-white/90 text-lg mb-8">
              Contact us today to discuss your business detailing needs and receive a customized quote 
              tailored to your specific requirements.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/business-quote" className="bg-white text-blue-600 hover:bg-blue-50 font-medium py-3 px-8 rounded-lg inline-block transition-colors">
                Request Business Quote
              </Link>
              <Link to="/contact" className="bg-transparent border-2 border-white text-white hover:bg-white/10 font-medium py-3 px-8 rounded-lg inline-block transition-colors">
                Contact Sales Team
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DealershipFleetPage;